import React, { FC, useEffect, useState } from 'react';
import { getDatePickerFormat, getDateFnsLocale } from 'utils/format';
import { Box, FormControl } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateRange } from '@mui/lab';
import { useTranslation } from 'react-i18next';
import { OrderOnDate } from 'models/invoice';

type DateFromToFilterProps = {
    dateFromTo: [Date | null, Date | null];
    onDateChange: (date: DateRange<Date>) => void;
    dateFilterType: string;
};
const DateFromToFilter: FC<DateFromToFilterProps> = ({
    dateFromTo,
    onDateChange,
    dateFilterType,
}: DateFromToFilterProps) => {
    const { t: translate } = useTranslation('orders');
    const [fromDate, setFromDate] = useState<Date | null>(null);
    const [toDate, setToDate] = useState<Date | null>(null);

    useEffect(() => {
        if (dateFromTo.length) {
            const [incomingFromDate, incomingToDate] = dateFromTo;
            const isInvalidRange = incomingFromDate && incomingToDate && incomingFromDate > incomingToDate;
            if (isInvalidRange) {
                setFromDate(null);
                setToDate(null);
                onDateChange([null, null]);
            } else {
                setFromDate(incomingFromDate);
                setToDate(incomingToDate);
            }
        }
    }, [dateFromTo]);

    const handleFromDateChange = (date: Date | null) => {
        setFromDate(date);
        const isToDateBeforeFromDate = date && toDate && date > toDate;
        if (isToDateBeforeFromDate) {
            setToDate(null);
            onDateChange([date, null]);
        } else if (date && toDate) {
            onDateChange([date, toDate]);
        }
    };
    const handleToDateChange = (date: Date | null) => {
        setToDate(date);
        if (fromDate && date) {
            onDateChange([fromDate, date]);
        }
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={getDateFnsLocale()}>
            <Box display="flex">
                <FormControl fullWidth sx={{ marginRight: 1 }}>
                    <DatePicker
                        format={getDatePickerFormat()}
                        disabled={dateFilterType === OrderOnDate.NO_DATE_FILTER}
                        onChange={handleFromDateChange}
                        label={translate('orders:DATE_FROM')}
                        value={fromDate}
                        maxDate={toDate || undefined}
                    />
                </FormControl>
                <FormControl fullWidth>
                    <DatePicker
                        format={getDatePickerFormat()}
                        disabled={dateFilterType === OrderOnDate.NO_DATE_FILTER}
                        onChange={handleToDateChange}
                        label={translate('orders:DATE_TO')}
                        value={toDate}
                        minDate={fromDate || undefined}
                    />
                </FormControl>
            </Box>
        </LocalizationProvider>
    );
};

export default DateFromToFilter;
